import axios from 'axios'
import { getHrAccessToken } from 'services/session/authSession'

// Create axios instance for chat API
const chatApiClient = axios.create({
  baseURL: process.env.REACT_APP_CHAT_API_URL || 'http://localhost:4300',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
})

// Request interceptor to add authentication token
chatApiClient.interceptors.request.use(
  async (config) => {
    const hr_access_token = getHrAccessToken()

    // Check if token exists before making request
    if (!hr_access_token) {
      console.warn('⚠️ No authentication token found - redirecting to login')

      return Promise.reject({
        type: 'NO_TOKEN',
        message: 'No authentication token found',
        isAuthError: true,
      })
    }

    config.headers['Authorization'] = `Bearer ${hr_access_token}`
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling with retry logic
// chatApiClient.interceptors.response.use(
//   (response) => {
//     return response
//   },
//   async (error) => {
//     if (error.response) {
//       const { status } = error.response

//       if (status === 401) {
//         console.error('🔒 Chat API: Authentication failed - Token expired or invalid')

//         // Clear expired tokens
//         cookies.remove('hr_access_token', { path: '/' })
//         cookies.remove('access_token', { path: '/' })

//         // Redirect to login with return URL
//         const currentPath = window.location.pathname + window.location.search
//         window.location.href =
//           '/auth/login?reason=session_expired&redirect=' + encodeURIComponent(currentPath)

//         // Return a structured error
//         return Promise.reject({
//           type: 'AUTH_ERROR',
//           message: 'Session expired. Please log in again.',
//           status: 401,
//           isAuthError: true,
//         })
//       } else if (status === 403) {
//         console.log('Chat API: Permission denied')
//         return Promise.reject({
//           type: 'PERMISSION_ERROR',
//           message: "You don't have permission to perform this action.",
//           status: 403,
//         })
//       } else if (status === 429) {
//         console.log('Chat API: Rate limit exceeded - too many requests')
//         const retryAfter = error.response.headers['retry-after'] || 60
//         console.log(`Retry after ${retryAfter} seconds`)

//         // Implement exponential backoff for rate limited requests
//         const config = error.config
//         if (!config._retryCount) {
//           config._retryCount = 0
//         }

//         if (config._retryCount < 3) {
//           config._retryCount++
//           const delay = Math.pow(2, config._retryCount) * 1000 // Exponential backoff
//           console.log(`Retrying request in ${delay}ms (attempt ${config._retryCount})`)

//           await new Promise((resolve) => setTimeout(resolve, delay))
//           return chatApiClient(config)
//         }

//         return Promise.reject({
//           type: 'RATE_LIMIT_ERROR',
//           message: 'Too many requests. Please wait and try again.',
//           status: 429,
//           retryAfter,
//         })
//       } else if (status === 500) {
//         console.log('Chat API: Server error')
//         return Promise.reject({
//           type: 'SERVER_ERROR',
//           message: 'Server error occurred. Please try again later.',
//           status: 500,
//         })
//       }
//     }

//     // Handle network errors (no response)
//     if (error.code === 'ECONNABORTED' || error.message === 'Network Error' || !error.response) {
//       console.error('🌐 Network error:', error.message)
//       return Promise.reject({
//         type: 'NETWORK_ERROR',
//         message: 'Network connection lost. Please check your internet.',
//         originalError: error,
//       })
//     }

//     return Promise.reject(error)
//   }
// )

// Helper function to extract data from response
const getData = (response) => response.data

// Get current user information
export const getCurrentUser = async () => {
  return chatApiClient.get('/users/me').then(getData)
}

// =============================================
// CONVERSATION API FUNCTIONS
// =============================================

// Get all conversations for the authenticated user
export const getAllConversations = async (limit = 20, offset = 0) => {
  return chatApiClient.get(`/conversations?limit=${limit}&offset=${offset}`).then(getData)
}

// Get conversation details by ID
export const getConversationById = async (conversationId) => {
  return chatApiClient.get(`/conversations/${conversationId}`).then(getData)
}

// Create a new conversation
export const createConversation = async (conversationData) => {
  return chatApiClient.post('/conversations', conversationData).then(getData)
}

// Update conversation settings
export const updateConversation = async (conversationId, updateData) => {
  return chatApiClient.patch(`/conversations/${conversationId}`, updateData).then(getData)
}

// Delete a conversation
export const deleteConversation = async (conversationId) => {
  return chatApiClient.delete(`/conversations/${conversationId}`).then(getData)
}

// Add participant to conversation
export const addParticipant = async (conversationId, participantId) => {
  return chatApiClient
    .post(`/conversations/${conversationId}/participants`, {
      participant_uuid: [participantId],
    })
    .then(getData)
}

// Remove participant from conversation
export const removeParticipant = async (conversationId, participantId) => {
  return chatApiClient
    .delete(`/conversations/${conversationId}/participants/${participantId}`)
    .then(getData)
}

// Make user an admin
export const makeAdmin = async (conversationId, targetUuid) => {
  return chatApiClient
    .post(`/conversations/${conversationId}/admins`, {
      user_uuid: targetUuid,
    })
    .then(getData)
}

// Demote admin
export const demoteAdmin = async (conversationId, targetUuid) => {
  return chatApiClient.delete(`/conversations/${conversationId}/admins/${targetUuid}`).then(getData)
}

// Get messages for a conversation
export const getConversationMessages = async (conversationId, limit = 50, offset = 0) => {
  return chatApiClient
    .get(`/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`)
    .then(getData)
}

// Pin a conversation
export const pinConversation = async (conversationId) => {
  return chatApiClient.patch(`/conversations/${conversationId}/pin`).then(getData)
}

// Unpin a conversation
export const unpinConversation = async (conversationId) => {
  return chatApiClient.patch(`/conversations/${conversationId}/unpin`).then(getData)
}

export const presignGroupImage = async (conversationId, fileData) => {
  return chatApiClient
    .post(`/conversations/${conversationId}/group-image/presign`, fileData)
    .then(getData)
}

export const completeGroupImageUpload = async (conversationId, uploadData) => {
  return chatApiClient
    .post(`/conversations/${conversationId}/group-image/complete`, uploadData)
    .then(getData)
}

export const getGroupImageUrl = async (conversationId) => {
  return chatApiClient.get(`/conversations/${conversationId}/group-image/url`).then(getData)
}

// Get unread count for a conversation
export const getUnreadCount = async (conversationId) => {
  return chatApiClient.get(`/conversations/${conversationId}/unread-count`).then(getData)
}

// Mark messages as read up to a specific message
export const markReadUpTo = async (conversationId, messageUuid) => {
  return chatApiClient
    .post(`/conversations/${conversationId}/read-up-to`, {
      message_uuid: messageUuid,
      include_readers: true,
    })
    .then(getData)
}

export const getMessageReaders = async (conversationId, messageUuid) => {
  if (!conversationId || !messageUuid) {
    return { readers: [], count: 0 }
  }
  return chatApiClient.get(`/conversations/${conversationId}/who-read/${messageUuid}`).then(getData)
}

// =============================================
// MESSAGE API FUNCTIONS
// =============================================

// Get all messages for a conversation
export const getMessages = async (conversationUuid, limit = 50, offset = 0) => {
  return chatApiClient
    .get(`/conversations/${conversationUuid}/messages?limit=${limit}&offset=${offset}`)
    .then(getData)
}

// Send a new message
export const sendMessage = async (messageData) => {
  console.log('🚀 chatApiService.sendMessage called with:', messageData)
  console.log(
    '🌐 Making POST request to:',
    process.env.REACT_APP_CHAT_API_URL || 'http://localhost:4300',
    '/messages'
  )
  return chatApiClient.post('/messages', messageData).then(getData)
}

// Get single message by ID
export const getMessageById = async (messageId) => {
  return chatApiClient.get(`/messages/${messageId}`).then(getData)
}

// Edit/update a message
export const updateMessage = async (messageId, updateData) => {
  return chatApiClient.patch(`/messages/${messageId}`, updateData).then(getData)
}

// Delete a message
export const deleteMessage = async (messageId) => {
  return chatApiClient.delete(`/messages/${messageId}`).then(getData)
}

// NEW: Pin a message in conversation
export const pinMessage = async (conversationId, messageUuid) => {
  return chatApiClient
    .patch(`/conversations/${conversationId}/pinned-message`, {
      message_uuid: messageUuid,
    })
    .then(getData)
}

// NEW: Unpin a message in conversation
export const unpinMessage = async (conversationId, messageUuid) => {
  return chatApiClient
    .patch(`/conversations/${conversationId}/pinned-message/remove`, {
      message_uuid: messageUuid,
    })
    .then(getData)
}

// Mark message as read
export const markMessageAsRead = async (messageId) => {
  return chatApiClient.patch(`/messages/${messageId}/read`).then(getData)
}

// Mark message as delivered
export const markMessageAsDelivered = async (messageId) => {
  return chatApiClient.patch(`/messages/${messageId}/delivered`).then(getData)
}

// Add reaction to message
export const addReaction = async (messageId, reaction) => {
  return chatApiClient
    .post(`/messages/${messageId}/reactions`, {
      // messageUuid: messageId,
      reaction,
    })
    .then(getData)
}

// Remove reaction from message
export const removeReaction = async (messageId, reaction) => {
  return chatApiClient.delete(`/messages/${messageId}/reactions/${reaction}`).then(getData)
}

export const forwardMessages = async (forwardData) => {
  console.log('🚀 chatApiService.forwardMessages called with:', forwardData)
  console.log(
    '🌐 Making POST request to:',
    process.env.REACT_APP_CHAT_API_URL || 'http://localhost:4300',
    '/messages/forward'
  )
  return chatApiClient.post('/messages/forward', forwardData).then(getData)
}

// Get conversations list for forwarding (reuse existing function)
export const getConversationsList = async (limit = 100, offset = 0) => {
  return getAllConversations(limit, offset)
}

// =============================================
// MEDIA API FUNCTIONS
// =============================================

// Get media for a conversation
export const getConversationMedia = async (conversationUuid, limit = 50, offset = 0) => {
  return chatApiClient
    .get(`/media?conversation_uuid=${conversationUuid}&limit=${limit}&offset=${offset}`)
    .then(getData)
}

// Upload media
export const uploadMedia = async (mediaData) => {
  return chatApiClient.post('/media', mediaData).then(getData)
}

// Get media by ID
export const getMediaById = async (mediaId) => {
  return chatApiClient.get(`/media/${mediaId}`).then(getData)
}

// Delete media
export const deleteMedia = async (mediaId) => {
  return chatApiClient.delete(`/media/${mediaId}`).then(getData)
}

export const presignChatMedia = async (conversationId, fileData, senderUuid) => {
  try {
    const requestBody = {
      conversation_uuid: conversationId,
      sender_uuid: senderUuid,
      file_name: fileData.fileName,
      mime_type: fileData.mimeType,
      size: parseInt(fileData.size) || 0,
    }

    const response = await chatApiClient.post('/messages/media/presign', requestBody)
    return response.data
  } catch (error) {
    console.error('Error presigning media:', error)
    throw error
  }
}

export const completeChatMediaUpload = async (conversationId, uploadData, senderUuid) => {
  try {
    const requestBody = {
      conversation_uuid: conversationId,
      sender_uuid: senderUuid,
      s3_key: uploadData.s3Key,
      file_name: uploadData.fileName,
      mime_type: uploadData.mimeType,
      size: uploadData.size,
    }

    const response = await chatApiClient.post('/messages/media/complete', requestBody)
    return response.data
  } catch (error) {
    console.error('Error completing media upload:', error)
    throw error
  }
}

export const uploadFileToS3 = async ({ presign, file, onProgress }) => {
  const formData = new FormData()

  // Add presigned fields
  if (presign.fields) {
    Object.entries(presign.fields).forEach(([key, value]) => {
      formData.append(key, value)
    })
  }

  formData.append('file', file.uri || file) // For web, file.uri is the File object

  try {
    const response = await fetch(presign.upload_url || presign.url, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`)
    }

    return {
      success: true,
      s3Key: presign.key,
    }
  } catch (error) {
    console.error('S3 upload error:', error)
    throw error
  }
}

export const getMediaSignedUrl = async (mediaUuid, ttl = 300) => {
  try {
    const response = await chatApiClient.get(`/messages/media/${mediaUuid}/signed-url?ttl=${ttl}`)
    return response.data
  } catch (error) {
    console.error('Error getting media signed URL:', error)
    throw error
  }
}

// =============================================
// TENANT API FUNCTIONS
// =============================================

// Get all tenants for the authenticated user
export const getAllTenants = async () => {
  return chatApiClient.get('/tenants').then(getData)
}

// Get tenant by ID
export const getTenantById = async (tenantId) => {
  return chatApiClient.get(`/tenants/${tenantId}`).then(getData)
}

// Create new tenant
export const createTenant = async (tenantData) => {
  return chatApiClient.post('/tenants', tenantData).then(getData)
}

// Update tenant
export const updateTenant = async (tenantId, updateData) => {
  return chatApiClient.patch(`/tenants/${tenantId}`, updateData).then(getData)
}

// Delete tenant
export const deleteTenant = async (tenantId) => {
  return chatApiClient.delete(`/tenants/${tenantId}`).then(getData)
}

// =============================================
// USER API FUNCTIONS
// =============================================

// Get user profile by ID
export const getUserById = async (userId) => {
  return chatApiClient.get(`/users/${userId}`).then(getData)
}

// Get current user profile
export const getUserProfile = async () => {
  return chatApiClient.get('/users/profile').then(getData)
}

// =============================================
// UTILITY FUNCTIONS
// =============================================

// Test HR API connectivity
export const testHrApiConnectivity = async (token) => {
  return chatApiClient.post('/test-hr-api', { token }).then(getData)
}

// Test authentication
export const testAuth = async () => {
  return chatApiClient.get('/test-auth').then(getData)
}

export default chatApiClient
